import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/context/AuthContext";
import { addPrescription, updatePrescription } from "@/lib/api";
import { PrescriptionDataTransformer, PrescriptionFormData } from "@/types/prescription";

// ─── Schema ───────────────────────────────────────────────────────────────────

const schema = z.object({
    id:          z.string().optional(),
    patient_id:  z.string().min(1, "Patient ID is required"),
    doctor_id:   z.string(),
    clinic_id:   z.string(),
    date:        z.string().min(1, "Date is required"),
    diagnosis:   z.string(),
    quick_notes: z.string(),
    advice:      z.string(),

    vitals: z.object({
        bp:      z.string().optional(),
        pulse:   z.string().optional(),
        spo2:    z.string().optional(),
        weight:  z.string().optional(),
        temp:    z.string().optional(),
        edema:   z.string().optional(),
        pallor:  z.string().optional(),
        icterus: z.string().optional(),
    }),

    complaints: z.array(
        z.object({
            complaint: z.string(),
            frequency: z.string().optional(),
            severity:  z.string().optional(),
            duration:  z.string().optional(),
            date:      z.string().optional(),
        })
    ),

    investigations: z.array(z.string()),

    medicines: z.array(
        z.object({
            drug_id:      z.number().nullable().optional(),
            brand_name:   z.string().optional(),
            generic_name: z.string().optional(),
            product:      z.string().optional(),
            dosage:       z.string().optional(),
            taken_when:   z.string().optional(),
            route:        z.string().optional(),
            duration:     z.string().optional(),
            quantity:     z.union([z.string(), z.number()]).optional(),
            notes:        z.string().optional(),
            // Drug meta — retained in form state for dosage option resolution.
            // Stripped from API payload by PrescriptionDataTransformer.toApiPayload().
            _drug_form:   z.string().optional(),
            _drug_brand:  z.string().optional(),
        })
    ),

    next_visit: z.object({
        date:  z.string().optional(),
        time:  z.string().optional(),
        notes: z.string().optional(),
    }).optional(),

    custom_fields: z.object({
        custom_1: z.string().optional(),
        custom_2: z.string().optional(),
        custom_3: z.string().optional(),
    }).optional(),
}).transform((data) => ({
    ...data,
    complaints:     data.complaints?.filter((c) => c.complaint?.trim())          || [],
    investigations: data.investigations?.filter((i) => i?.trim())                || [],
    // Strip drug meta before the transformed value leaves the schema
    medicines: data.medicines
        ?.filter((m) => m.product?.trim())
        .map(({ _drug_form, _drug_brand, ...rest }) => rest) || [],
}));

type FormDataType = z.infer<typeof schema>;

// ─── Types ────────────────────────────────────────────────────────────────────

interface UsePrescriptionFormOptions {
    mode?: "create" | "edit";
    patientId: string;
    initialData?: Partial<PrescriptionFormData>;
    // Receives the saved prescription's id — used to flush any documents
    // staged before the prescription existed (see AttachmentsSection /
    // PendingAttachments). Existing callers that ignore the argument keep
    // working unchanged.
    onSaved?: (prescriptionId?: string) => void;
    onCancel?: () => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const usePrescriptionForm = ({
                                        mode = "create",
                                        patientId,
                                        initialData,
                                        onSaved,
                                        onCancel,
                                    }: UsePrescriptionFormOptions) => {
    const { currentUser } = useAuth();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasChanges, setHasChanges]     = useState(false);
    const hasInitialResetRef              = useRef(false);

    // Stable refs so callbacks don't re-create when parent passes new inline functions
    const onSavedRef  = useRef(onSaved);
    const onCancelRef = useRef(onCancel);
    useEffect(() => { onSavedRef.current  = onSaved;  }, [onSaved]);
    useEffect(() => { onCancelRef.current = onCancel; }, [onCancel]);

    // Default values
    const defaultValues = useMemo((): PrescriptionFormData => {
        if (initialData) {
            return PrescriptionDataTransformer.toFormData(initialData as any, patientId);
        }
        return PrescriptionDataTransformer.createEmpty(
            patientId,
            currentUser?.doctor_id || "",
            currentUser?.clinic_id || ""
        );
    }, [mode, initialData, patientId, currentUser?.doctor_id, currentUser?.clinic_id]);

    const form = useForm<PrescriptionFormData>({
        resolver: zodResolver(schema) as any,
        defaultValues,
        mode: "onChange",
    });

    const { reset, getValues, formState: { errors, isDirty }, watch } = form;
    const watchedValues = watch();

    // One-time initialisation with data
    useEffect(() => {
        if (!hasInitialResetRef.current) {
            if (initialData) {
                const formData = PrescriptionDataTransformer.toFormData(initialData as any, patientId);
                reset(formData);
                setHasChanges(false);
            }
            hasInitialResetRef.current = true;
        }
    }, [mode, initialData, patientId, reset]);

    // Track changes
    useEffect(() => {
        setHasChanges(isDirty);
    }, [isDirty]);

    // Meaningful content check.
    // Intentionally omits watchedValues — watch() returns a new object every render,
    // which would cause submitForm to be recreated on every keystroke, triggering the
    // onFormReady useEffect in PrescriptionForm on every render (infinite loop).
    // isDirty is sufficient: false → no content possible; true → read values once via getValues().
    // When the user clears everything, isDirty returns to false and hasValidChanges → false.
    const hasValidChanges = useMemo(() => {
        if (!isDirty) return false;
        const values = getValues();
        return (
            values.complaints?.some((c) => typeof c.complaint === "string" && c.complaint.trim()) ||
            (typeof values.diagnosis === "string" && !!values.diagnosis.trim()) ||
            values.medicines?.some((m) => (m.brand_name || m.product)?.trim()) ||
            values.investigations?.some((i) => typeof i === "string" && i.trim()) ||
            (typeof values.advice === "string" && !!values.advice.trim()) ||
            (typeof values.quick_notes === "string" && !!values.quick_notes.trim()) ||
            Object.values(values.vitals || {}).some((v) => v && v !== "None") ||
            Object.values(values.custom_fields || {}).some((v) => typeof v === "string" && v.trim())
        );
    }, [isDirty, getValues]);

    // Submit
    const handleSubmit = useCallback(
        async (data: PrescriptionFormData) => {
            if (!hasValidChanges && mode === "create") {
                toast({
                    title: "No Content",
                    description: "Please add some content before saving",
                    variant: "destructive",
                });
                return;
            }

            setIsSubmitting(true);
            try {
                const prescriptionData = PrescriptionDataTransformer.toApiPayload(data, mode === "edit");

                let savedId: string | undefined;
                if (mode === "edit" && data.id) {
                    await updatePrescription({ ...prescriptionData, id: data.id });
                    savedId = data.id;
                    toast({ title: "Updated Successfully", description: "Prescription has been updated" });
                } else {
                    const created = await addPrescription(prescriptionData);
                    savedId = created?.id;
                    toast({ title: "Saved Successfully", description: "New prescription has been created" });
                }

                onSavedRef.current?.(savedId);
            } catch (error: any) {
                console.error("Error saving prescription:", error);
                toast({
                    title: "Error",
                    description: error?.message || "Failed to save prescription. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsSubmitting(false);
            }
        },
        [hasValidChanges, mode]
    );

    // Cancel with unsaved changes guard
    const handleCancel = useCallback(() => {
        if (hasChanges) {
            const confirmed = window.confirm("You have unsaved changes. Are you sure you want to cancel?");
            if (!confirmed) return;
        }
        onCancelRef.current?.();
    }, [hasChanges]);

    const submitForm = useCallback(() => {
        form.handleSubmit(handleSubmit)();
    }, [form.handleSubmit, handleSubmit]);

    return {
        form,
        isSubmitting,
        hasChanges,
        hasValidChanges,
        errors,
        isDirty,
        submitForm,
        handleCancel,
        watchedValues,
        getValues,
        mode,
        patientId,
    };
};